import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon, 
  CultureIcon, 
  ArtIcon, 
  MusicIcon, 
  TheaterIcon, 
  CinemaIcon, 
  FestivalIcon, 
  SportIcon, 
  WorkshopIcon, 
  MarketIcon, 
  ConferenceIcon, 
  NatureIcon, 
  FamilyIcon,
  StarIcon,
  AccessibleIcon,
  WeatherIcon,
  EuroIcon,
  ClockIcon,
  LocationIcon,
  CalendarIcon,
  BuildingIcon
} from './icons/NavIcons';

/**
 * Composant EventCard
 * Affiche une tuile d'événement réutilisable
 * 
 * @param {Object} event - L'événement à afficher
 * @param {Function} onClick - Fonction appelée au clic
 * @param {boolean} isPast - Indique si l'événement est passé
 * @param {boolean} isFavorite - Indique si l'événement est en favori
 * @param {boolean} isCreator - Indique si l'utilisateur est le créateur de l'événement
 * @param {Array<number>} stats - Statistiques des 7 derniers jours (vues ou participants)
 * @param {string} categoryColorCode - Code couleur de la catégorie
 */
const EventCard = ({ event, onClick, isPast = false, isFavorite = false, isCreator = false, stats = [], categoryColorCode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Mapping des catégories vers les icônes
  const categoryIcons = {
    'culture': CultureIcon,
    'art': ArtIcon,
    'music': MusicIcon,
    'theater': TheaterIcon,
    'cinema': CinemaIcon,
    'festival': FestivalIcon,
    'sport': SportIcon,
    'atelier': WorkshopIcon,
    'workshop': WorkshopIcon,
    'market': MarketIcon,
    'conference': ConferenceIcon,
    'nature': NatureIcon,
    'family': FamilyIcon
  };

  // Récupérer le composant d'icône pour la catégorie
  const CategoryIcon = event.category?.name ? categoryIcons[event.category.name.toLowerCase()] : null;

  // Debug: vérifier les props reçues ET la couleur de catégorie
  // console.log('[EventCard]', {
  //   eventId: event.id,
  //   eventTitle: event.title,
  //   isFavorite,
  //   categoryName: event.category?.name,
  //   categoryColorCodeProp: categoryColorCode,
  //   categoryColorCodeFromEvent: event.category?.color_code,
  //   isCreator,
  //   hasStats: stats && stats.length > 0,
  //   stats
  // });

  // Calculer les coordonnées du graphique sparkline
  const renderSparkline = () => {
    if (!stats || stats.length === 0) return null;
    
    const max = Math.max(...stats, 1);
    const width = 60;
    const height = 20;
    const points = stats.map((value, index) => {
      const x = (index / (stats.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height} className="inline-block">
        <polyline
          points={points}
          fill="none"
          stroke="#2894D8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const handleStatsClick = (e) => {
    e.stopPropagation(); // Empêcher la propagation au onClick parent
    navigate(`/event/${event.id}/stats`);
  };

  // Calculer la durée en jours
  const calculateDuration = () => {
    if (!event.start_date || !event.end_date) return null;
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 1 ? diffDays : null;
  };

  const duration = calculateDuration();

  return (
    <div 
      onClick={onClick}
      className={`bg-white p-3 rounded-lg shadow-sm border flex flex-col cursor-pointer relative border-l-4 
        ${isPast ? 'opacity-60' : ''} 
        ${event.boost 
          ? 'border-2 border-[#2894D8] shadow-[0_0_15px_rgba(40,148,216,0.4)] animate-pulse-subtle' 
          : 'border-gray-100'
        }`}
      // style={{ 
      //   borderLeftColor: categoryColorCode || '#cccccc',
      //   ...(event.boost && {
      //     boxShadow: '0 0 20px rgba(40, 148, 216, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      //   })
      // }}
    >
      {/* Badge "Mis en avant" pour les événements boostés */}
      {event.boost && (
        <div className="absolute -top-2 -right-3 bg-[#2894D8] text-white text-xs px-2 py-1 rounded-full shadow-md z-10 flex items-center gap-1">
          <StarIcon className="w-4 h-4" />
          {/* <span>{t('eventList.featured')}</span> */}
        </div>
      )}
      
      <div className="flex items-start">
        {CategoryIcon ? (
          <div style={{ color: categoryColorCode || '#2894D8' }}>
            <CategoryIcon className="w-8 h-8 mr-3 flex-shrink-0" />
          </div>
        ) : (
          <span className="text-2xl mr-3">{event.category?.icon}</span>
        )}
        <div className="flex-1">
          {/* Titre */}
          <h3 className="text-h3 font-sans" style={{ color: '#1c1c1e' }}>{event.title}</h3>
          <p className="text-gray-600 text-sm">
            {/* Catégorie */}
            {event.category?.name
              ? <span style={{ color: categoryColorCode || '#666666' }}>
                  {t(`categories.${event.category.name}`)}
                </span>
              : ''}
            {/* Horaire et adresse */}
            {(event.start_time || event.formatted_address || event.city) && <br />}
            <span className="flex flex-col gap-1">
              {event.start_time && (
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4 inline-block" />
                  {event.start_time.slice(0, 5)}
                  {event.end_time && ` - ${event.end_time.slice(0, 5)}`}
                </span>
              )}
              {event.location_name && (
                <span className="flex items-center gap-1">
                  <BuildingIcon className="w-4 h-4 inline-block flex-shrink-0" />
                  {event.location_name}
                </span>
              )}
              {(event.formatted_address || event.city) && (
                <span className="flex items-center gap-1">
                  <LocationIcon className="w-4 h-4 inline-block flex-shrink-0" />
                  {[event.formatted_address, event.city]
                    .filter(Boolean)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .join(', ')
                  }
                </span>
              )}
            </span>
          </p>
        </div>
        {/* Badges alignés à droite */}
          <div className="flex flex-col gap-1 items-end ml-2">
            {duration && (
              <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 border border-gray-200">
                <CalendarIcon className="w-3.5 h-3.5" />
                {duration}{t('eventCard.days')}
              </span>
            )}
            {event.is_recurring && (
              <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                🔄
              </span>
            )}
            {event.is_paid && (
              <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200  text-gray-700 flex items-center">
                <EuroIcon className="w-4 h-4" />
              </span>
            )}
            {event.weather_dependent && (
              <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200  text-gray-700 flex items-center">
                <WeatherIcon className="w-4 h-4" />
              </span>
            )}
            {event.is_accessible && (
              <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200  text-gray-700 flex items-center">
                <AccessibleIcon className="w-4 h-4" />
              </span>
            )}
            {event.age_group?.name && (
              <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200  text-gray-700">
                {/* 🧒  */}
                {t(`ageGroups.${event.age_group.name}`)}
              </span>
            )}
            {isFavorite && (
              <span className="bg-red-50 px-2 py-0.5 rounded-full text-xs border border-red-200 flex items-center">
                <HeartIcon className="w-4 h-4 text-red-500 fill-red-500" />
              </span>
            )}
          </div>
              </div>

      {/* Mini graphique statistiques pour le créateur */}
      {isCreator && stats && stats.length > 0 && (
        <div 
          onClick={handleStatsClick}
          className="absolute bottom-2 right-2 bg-gray-50 px-2 py-1 rounded border border-gray-200 flex items-center gap-1 z-20 hover:bg-gray-100 cursor-pointer transition-colors"
        >
          <span className="text-xs text-gray-600">{t('eventCard.stats7days')}</span>
          {renderSparkline()}
        </div>
      )}
      
      {/* Badge cliquable pour les créateurs même sans stats */}
      {/* {isCreator && (!stats || stats.length === 0) && (
        <div 
          onClick={handleStatsClick}
          className={`absolute bottom-2 ${isFavorite ? 'right-10' : 'right-2'} bg-gray-100 px-2 py-1 rounded border border-gray-300 text-xs z-20 hover:bg-gray-200 cursor-pointer transition-colors flex items-center gap-1`}
        >
          <span>📊</span>
          <span className="text-gray-600">{t('eventCard.viewStats')}</span>
        </div>
      )} */}
    </div>
  );
};

export default EventCard;
